import { useCallback } from 'react';
import saveFormBehavior from '../../../../nativeBehaviors/saveFormBehavior';
import store from '../../../../reducers/storeRegistration';
import logger from '../../../../utils/logger';
import aiNativeBehaviorEvaluator from '../../../../nativeBehaviors/aiNativeBehaviorEvaluator';

/**
 * Custom hook for running followup investigation
 * @param {string} followupId - ID of the followup
 * @param {Object} followup - Followup data
 * @param {Object} content - Form content
 * @param {string} path - Content path
 * @param {Object} formActions - Form actions
 * @param {string} reduxEntity - Redux entity name
 * @param {Function} notificationActions - Notification actions
 * @param {boolean} pendingOperation - Whether a pending operation is in progress
 * @param {Function} setIsRunning - State setter for running state
 * @returns {Function} - runInvestigation function
 */
const useFollowupInvestigation = ({
  followupId,
  followup,
  content,
  path,
  formActions,
  reduxEntity,
  notificationActions,
  pendingOperation,
  setIsRunning
}) => {
  const runInvestigation = useCallback(async e => {
    if (e) {
      e.stopPropagation();
    }
    if (pendingOperation) {
      notificationActions.info({
        message: 'Waiting for changes to save.'
      }, {
        autoClose: 3000
      });
      return;
    }
    setIsRunning(true);
    try {
      // Call qualityAgentFollowUp directly via AI service
      const suggestion = {
        id: followupId,
        priority: followup.priority,
        description: followup.description,
        query: followup.query,
        reasoning: followup.reasoning
      };
      const originalContext = {
        project: (content === null || content === void 0 ? void 0 : content.title) || '',
        assessment: 'needs_attention',
        assessmentMessage: 'Found data quality issues',
        relatedFindings: ''
      };
      const response = await aiNativeBehaviorEvaluator.qualityAgentFollowUp(suggestion, originalContext);

      // Transform and save the analysis result
      const result = response.result || response;
      const {
        analysis,
        execution
      } = result;
      const {
        summary,
        recommendations
      } = analysis;

      // Format recommendations array as bulleted string
      let recommendationsString = '';
      if (recommendations && recommendations.length > 0) {
        recommendationsString = `• ${recommendations.join('\n• ')}`;
      }
      const analysisContent = {
        summary,
        recommendations: recommendationsString,
        execution
      };

      // Save analysis to content path
      const state = store.getState();
      const {
        active
      } = state[reduxEntity];
      await formActions.change(active.id, `content${path}/suggestedFollowups/${followupId}/analysis`, analysisContent);
      // eslint-disable-next-line import/no-named-as-default-member
      await saveFormBehavior.evaluate({}, reduxEntity);
    } catch (error) {
      logger('Error running followup investigation:', error);
      notificationActions.info({
        message: `Error running investigation: ${error.message || 'Unknown error'}`
      }, {
        autoClose: 5000
      });
    } finally {
      setIsRunning(false);
    }
  }, [followupId, followup, content, path, formActions, reduxEntity, notificationActions, pendingOperation, setIsRunning]);
  return runInvestigation;
};
export default useFollowupInvestigation;